import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

/**
 * Every change a staff member makes writes an **audit entry**, so every mutation invalidates `['audit']`
 * whether it remembers to or not.
 *
 * The narrow bug was that toggling a challenge refreshed `['challenges']` and not `['audit']`, so the audit
 * page served a cached list and the change appeared only after a full page reload (R0070). Adding the one
 * missing line would have fixed the report and left the next mutation to make the same mistake —
 * `AccountActions` had it right, `ChallengesPage` did not, and nothing marked the difference.
 *
 * So the rule is structural rather than remembered: name the queries **your** change affects, and the
 * audit trail is taken care of. If a mutation ever legitimately writes no audit entry, invalidating a key
 * nobody is watching costs one cache eviction — far less than the class of bug it removes.
 */
export function useAdminMutation<TResult, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
  invalidates: Array<string>,
  onSuccess?: (result: TResult) => void,
): UseMutationResult<TResult, Error, TVariables> {
  const queryClient = useQueryClient();

  return useMutation<TResult, Error, TVariables>({
    mutationFn,
    onSuccess: (result: TResult): void => {
      for (const key of [...invalidates, 'audit']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }

      onSuccess?.(result);
    },
  });
}
